import { useState } from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'framer-motion'
import { useRef } from 'react'
import * as yup from 'yup'

// Validation schema using yup
const validationSchema = yup.object().shape({
  name: yup
    .string()
    .trim()
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name must be less than 50 characters')
    .required('Name is required'),
  email: yup
    .string()
    .trim()
    .email('Please enter a valid email address')
    .required('Email is required'),
  message: yup
    .string()
    .trim()
    .min(10, 'Message must be at least 10 characters')
    .max(1000, 'Message must be less than 1000 characters')
    .required('Message is required')
})

/**
 * Contact section component - matching original kardstech.com design
 * Features: Contact form with poker cards background, "Join the Party" theme
 */
const Contact = () => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-50px" })
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null)
  const [validationErrors, setValidationErrors] = useState({})

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { 
      opacity: 0, 
      backgroundColor: 'transparent',
      border: 'none'
    },
    visible: {
      opacity: 1,
      backgroundColor: 'transparent',
      border: 'none',
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear validation error for this field when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: null
      }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Clear previous validation errors
    setValidationErrors({})
    setSubmitStatus(null)
    
    try {
      // Validate form data using yup schema
      await validationSchema.validate(formData, { abortEarly: false })
      
      // If validation passes, proceed with submission
      setIsSubmitting(true)
      
      // Simulate form submission
      setTimeout(() => {
        setIsSubmitting(false)
        setSubmitStatus('success')
        setFormData({ name: '', email: '', message: '' })
        
        // Reset status after 3 seconds
        setTimeout(() => {
          setSubmitStatus(null)
        }, 3000)
      }, 2000)
      
    } catch (validationError) {
      // Handle validation errors
      const errors = {}
      
      if (validationError.inner) {
        validationError.inner.forEach(error => {
          errors[error.path] = error.message
        })
      }
      
      setValidationErrors(errors)
      setSubmitStatus('error')
    }
  }


  return (
    <section 
      id="contact" 
      className="relative overflow-hidden"
      style={{ 
        backgroundColor: 'transparent', // Transparent since wrapper has background
        minHeight: '100vh'
      }}
      ref={ref}
    >
      
      <div 
        className="relative flex items-center justify-center"
        style={{ 
          zIndex: 10,
          minHeight: '100vh',
          paddingBottom: '0px', // No space for footer
          marginBottom: '0px' // Ensure no margin interferes with footer
        }}
      >
        <motion.div
          className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          style={{ background: 'transparent' }}
        >
          {/* Title Section */}
          <motion.div 
            className="mb-12"
            variants={itemVariants}
            style={{ background: 'transparent' }}
          >
            <h2 
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-8"
              style={{ 
                fontFamily: 'Sequel100-65, sans-serif',
                color: '#ffffff',
                textTransform: 'none',
                lineHeight: '1.1',
                background: 'transparent'
              }}
            >
              Contact us -<br />
              Join the<br />
              Party
            </h2>
          </motion.div>

          {/* Contact Form */}
          <motion.div 
            className="max-w-md mx-auto"
            variants={itemVariants}
            style={{ background: 'transparent' }}
          >
                        <form onSubmit={handleSubmit}>
                          {/* Name Input */}
                          <div style={{ minHeight: '80px', marginBottom: '16px' }}>
                            <motion.input
                              type="text"
                              name="name"
                              placeholder="Name"
                              value={formData.name}
                              onChange={handleInputChange}
                              className="w-full px-6 py-4 text-white placeholder-gray-400"
                              style={{
                                backgroundColor: 'transparent',
                                border: `2px solid ${validationErrors.name ? '#ff4444' : '#00ffff'}`,
                                borderRadius: '8px',
                                fontSize: '16px',
                                fontFamily: 'Poppins, sans-serif'
                              }}
                              whileFocus={{ 
                                scale: 1.02,
                                boxShadow: `0 0 20px ${validationErrors.name ? 'rgba(255, 68, 68, 0.3)' : 'rgba(0, 255, 255, 0.3)'}`
                              }}
                              transition={{ type: "spring", stiffness: 300 }}
                            />
                            {validationErrors.name && (
                              <p className="text-red-400 text-sm mt-1 text-left">{validationErrors.name}</p>
                            )}
                          </div>

              {/* Email Input */}
              <div style={{ minHeight: '80px', marginBottom: '16px' }}>
                <motion.input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-6 py-4 text-white placeholder-gray-400"
                  style={{
                    backgroundColor: 'transparent',
                    border: `2px solid ${validationErrors.email ? '#ff4444' : '#00ffff'}`,
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontFamily: 'Poppins, sans-serif'
                  }}
                  whileFocus={{ 
                    scale: 1.02,
                    boxShadow: `0 0 20px ${validationErrors.email ? 'rgba(255, 68, 68, 0.3)' : 'rgba(0, 255, 255, 0.3)'}`
                  }}
                  transition={{ type: "spring", stiffness: 300 }}
                />
                {validationErrors.email && (
                  <p className="text-red-400 text-sm mt-1 text-left">{validationErrors.email}</p>
                )}
              </div>

              {/* Message Input */}
              <div style={{ minHeight: '120px', marginBottom: '16px' }}>
                <motion.textarea
                  name="message"
                  placeholder="Message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-6 py-4 text-white placeholder-gray-400 resize-none"
                  style={{
                    backgroundColor: 'transparent',
                    border: `2px solid ${validationErrors.message ? '#ff4444' : '#00ffff'}`,
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontFamily: 'Poppins, sans-serif'
                  }}
                  whileFocus={{ 
                    scale: 1.02,
                    boxShadow: `0 0 20px ${validationErrors.message ? 'rgba(255, 68, 68, 0.3)' : 'rgba(0, 255, 255, 0.3)'}`
                  }}
                  transition={{ type: "spring", stiffness: 300 }}
                />
                {validationErrors.message && (
                  <p className="text-red-400 text-sm mt-1 text-left">{validationErrors.message}</p>
                )}
              </div>

                          {/* Actually Working Send Button */}
                          <div className="mt-6 mb-4">
                            <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    width: '300px',
                    height: '50px',
                    backgroundColor: '#E5FF47',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    color: '#000000',
                    textTransform: 'uppercase',
                    margin: '0 auto',
                    display: 'block'
                  }}
                  onClick={handleSubmit}
                >
                  {isSubmitting ? 'Sending...' : 'Send →'}
                </button>
              </div>

                          {/* Success Message Only */}
                          {submitStatus === 'success' && (
                            <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="bg-green-900/80 border border-green-400 text-green-300 px-4 py-3 rounded-lg mt-4"
                              style={{ background: 'rgba(0, 100, 0, 0.8)' }}
                            >
                              Thank you! Your message has been sent!
                            </motion.div>
                          )}
            </form>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}

export default Contact
